import React, { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import TopNav from "@/components/TopNav";
import { api } from "@/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";

export default function SessionDetailPage() {
  const { sessionId } = useParams();
  const [session, setSession] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let mounted = true;
    const run = async () => {
      setLoading(true);
      setError("");
      try {
        const res = await api.get(`/pod-sessions/${sessionId}`);
        if (!mounted) return;
        setSession(res.data);
      } catch (e) {
        if (!mounted) return;
        setError("Could not load session.");
      } finally {
        if (mounted) setLoading(false);
      }
    };

    run();
    return () => {
      mounted = false;
    };
  }, [sessionId]);

  const mapLink = useMemo(() => {
    if (!session) return "";
    return `https://www.google.com/maps?q=${session.location.lat},${session.location.lng}`;
  }, [session]);

  if (error) {
    return (
      <div data-testid="session-detail-error-page" className="min-h-screen bg-gradient-to-b from-slate-50 via-white to-slate-100 text-slate-900">
        <TopNav />
        <div className="mx-auto max-w-4xl px-4 py-10">
          <div className="text-sm text-rose-700" data-testid="session-detail-error">{error}</div>
          <Link to="/dashboard" data-testid="session-detail-back-link" className="text-sky-700 hover:text-sky-900 text-sm">
            Back
          </Link>
        </div>
      </div>
    );
  }

  if (loading || !session) {
    return (
      <div data-testid="session-detail-loading-page" className="min-h-screen bg-gradient-to-b from-slate-50 via-white to-slate-100 text-slate-900">
        <TopNav />
        <div className="mx-auto max-w-4xl px-4 py-10 text-sm text-slate-600" data-testid="session-detail-loading">
          Loading…
        </div>
      </div>
    );
  }

  const started = new Date(session.started_at).toLocaleString();
  const ended = session.ended_at ? new Date(session.ended_at).toLocaleString() : "—";

  const videos = session.media?.videos || session.media?.video ? (session.media?.videos || [session.media?.video].filter(Boolean)) : [];
  const photos = session.media?.photos || [];

  const typeBadgeClass =
    session.activity_type === "loading"
      ? "bg-emerald-50 text-emerald-700 border-emerald-200"
      : "bg-amber-50 text-amber-700 border-amber-200";

  return (
    <div data-testid="session-detail-page" className="min-h-screen bg-gradient-to-b from-slate-50 via-white to-slate-100 text-slate-900">
      <TopNav />
      <div className="mx-auto max-w-4xl px-4 py-8 space-y-6">
        <div className="flex items-center justify-between gap-3">
          <div>
            <div className="text-2xl font-semibold" data-testid="session-detail-title">Session {session.session_id}</div>
            <div className="mt-1 text-sm text-slate-600" data-testid="session-detail-subtitle">Start: {started} • End: {ended}</div>
          </div>
          <Badge data-testid="session-detail-type-badge" className={typeBadgeClass} variant="outline">
            {session.activity_type}
          </Badge>
        </div>

        <Card data-testid="session-detail-summary-card" className="border-slate-200 bg-white shadow-sm">
          <CardHeader>
            <CardTitle data-testid="session-detail-summary-title">Summary</CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            <div className="text-sm text-slate-800" data-testid="session-detail-cases">Cases: {session.counters?.cases ?? 0}</div>
            <div className="text-sm text-slate-800" data-testid="session-detail-sacks">Sacks: {session.counters?.sacks ?? 0}</div>
            <div className="text-sm text-slate-800" data-testid="session-detail-drive-status">Drive status: {session.drive_upload_status}</div>
            {session.drive_error ? (
              <div className="text-sm text-rose-700" data-testid="session-detail-drive-error">{session.drive_error}</div>
            ) : null}
            {session.place_name ? (
              <div className="text-sm text-slate-800" data-testid="session-detail-place">Place: {session.place_name}</div>
            ) : null}
            <div className="text-sm text-slate-800" data-testid="session-detail-location">
              Location: {session.location.lat.toFixed(6)}, {session.location.lng.toFixed(6)} (±{Math.round(session.location.accuracy_m)}m)
            </div>
            <a data-testid="session-detail-map-link" href={mapLink} target="_blank" rel="noreferrer" className="text-sm text-sky-700 hover:text-sky-900">
              Open in Maps
            </a>
            {session.notes ? (
              <div className="text-sm text-slate-800" data-testid="session-detail-notes">Notes: {session.notes}</div>
            ) : null}
          </CardContent>
        </Card>

        <Card data-testid="session-detail-media-card" className="border-slate-200 bg-white shadow-sm">
          <CardHeader>
            <CardTitle data-testid="session-detail-media-title">Media</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <div className="text-sm text-slate-700" data-testid="session-detail-videos-label">Videos</div>
              {videos.length === 0 ? (
                <div className="text-sm text-slate-600" data-testid="session-detail-videos-empty">No videos uploaded yet.</div>
              ) : (
                <div className="space-y-2" data-testid="session-detail-videos-list">
                  {videos.map((v, idx) => (
                    <a
                      key={v.drive_file_id || idx}
                      data-testid={`session-detail-video-link-${idx}`}
                      href={v.drive_web_view_link || "#"}
                      target="_blank"
                      rel="noreferrer"
                      className="block rounded-2xl border border-slate-200 bg-white p-4 hover:bg-slate-50"
                    >
                      <div className="text-sm text-slate-900" data-testid={`session-detail-video-name-${idx}`}>{v.name || `clip_${String(idx + 1).padStart(3, "0")}`}</div>
                      <div className="mt-1 text-xs text-slate-600" data-testid={`session-detail-video-mime-${idx}`}>{v.mime}</div>
                    </a>
                  ))}
                </div>
              )}
            </div>

            <div className="space-y-2">
              <div className="text-sm text-slate-700" data-testid="session-detail-photos-label">Photos</div>
              {photos.length === 0 ? (
                <div className="text-sm text-slate-600" data-testid="session-detail-photos-empty">No photos.</div>
              ) : (
                <div className="grid gap-3 sm:grid-cols-2" data-testid="session-detail-photos-grid">
                  {photos.map((p, idx) => (
                    <a
                      key={p.drive_file_id || idx}
                      data-testid={`session-detail-photo-link-${idx}`}
                      href={p.drive_web_view_link || "#"}
                      target="_blank"
                      rel="noreferrer"
                      className="rounded-2xl border border-slate-200 bg-white p-4 hover:bg-slate-50"
                    >
                      <div className="text-sm text-slate-900" data-testid={`session-detail-photo-name-${idx}`}>{p.name}</div>
                      <div className="mt-1 text-xs text-slate-600" data-testid={`session-detail-photo-mime-${idx}`}>{p.mime}</div>
                    </a>
                  ))}
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        <Link to="/dashboard" data-testid="session-detail-back-to-dashboard" className="text-sm text-sky-700 hover:text-sky-900">
          Back to dashboard
        </Link>
      </div>
    </div>
  );
}
